import { motion } from 'framer-motion';
import { ChevronRight, Clock, Swords, Trophy } from 'lucide-react';
import { competitions, journey } from '../data/competitions';
import { loadJSON, storageKeys } from '../storage';

export default function CompeteDashboard() {
  return (
    <div className="mx-auto max-w-6xl px-5 pb-20">
      {/* Hero */}
      <section className="pt-12 text-center md:pt-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold text-amber-200">
            <Swords className="size-3.5" />
            Taa Sandbox Compete Arena
          </p>
          <h1 className="mt-5 text-5xl font-black tracking-tight text-white md:text-6xl">Taa Arena</h1>
          <h2 className="mt-3 text-xl font-extrabold md:text-2xl">
            <span className="text-cyan-300">Train.</span> <span className="text-sky-400">Simulate.</span>{' '}
            <span className="text-amber-400">Compete.</span> <span className="text-violet-400">Improve.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-300/90">
            Build your robot, test it in realistic physics, understand the competition rules, and improve
            your strategy with the help of Taa Bot.
          </p>
        </motion.div>

        {/* Journey strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-x-1 gap-y-2"
        >
          {journey.map((step, i) => (
            <span key={step} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400">
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-slate-300">
                {step}
              </span>
              {i < journey.length - 1 && <ChevronRight className="size-3.5 text-slate-600" />}
            </span>
          ))}
        </motion.div>
      </section>

      {/* Challenge cards */}
      <section className="mt-12">
        <h3 className="text-center text-2xl font-extrabold text-white md:text-3xl">Choose Your Challenge</h3>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {competitions.map((comp, i) => {
            const best = loadJSON<number | null>(storageKeys.bestScore(comp.id), null);
            return (
              <motion.a
                key={comp.id}
                href={comp.comingSoon ? undefined : `#/compete/${comp.id}`}
                onClick={(e) => {
                  if (comp.comingSoon) e.preventDefault();
                }}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                aria-disabled={comp.comingSoon}
                className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-navy-900/70 p-7 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${comp.hoverBorder} ${comp.hoverGlow} ${comp.comingSoon ? 'cursor-default' : ''}`}
              >
                {comp.comingSoon && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-navy-950/75 backdrop-blur-[2px]">
                    <span
                      className={`rounded-2xl px-6 py-3 text-xl font-extrabold uppercase tracking-widest ring-2 sm:text-2xl ${comp.chip}`}
                    >
                      Coming Soon
                    </span>
                  </div>
                )}
                {comp.image && (
                  <img
                    src={comp.image}
                    alt=""
                    className="pointer-events-none absolute inset-0 size-full object-cover opacity-20 transition-opacity duration-300 group-hover:opacity-30"
                  />
                )}
                <div className="relative">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h4 className={`text-2xl font-extrabold ${comp.text}`}>{comp.name}</h4>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${comp.chip}`}>
                        {comp.difficulty}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                        <Clock className="size-3" />
                        {comp.buildTime}
                      </span>
                    </div>
                  </div>

                  <p className="mt-3 text-sm leading-relaxed text-slate-300/90">{comp.tagline}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {comp.skills.map((skill) => (
                      <span key={skill} className="rounded-md bg-white/5 px-2 py-1 text-[11px] font-medium text-slate-400">
                        {skill}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold ${comp.button}`}>
                      Enter {comp.arenaName}
                      <ChevronRight className="size-4" />
                    </span>
                    {best !== null && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300">
                        <Trophy className="size-3.5" />
                        Best: {best} pts
                      </span>
                    )}
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </section>
    </div>
  );
}
